import { BaseDialog } from '@/components/BaseDialog'
import { ImageUpload } from '@/components/ImageUpload'
import { OnlineStatusDot } from '@/components/ItemOnlineStatus'
import { LogTimeline } from '@/components/LogTimeline'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { type SearchableSelectGroup } from '@/components/ui/searchable-select'
import { Textarea } from '@/components/ui/textarea'
import { itemStatusConfig } from '@/constants/item'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { useGetDepartmentOptions } from '@/features/departments/hooks/useDepartment'
import { ItemLocations } from '@/features/item-location/components/ItemLocations'
import { useGetItemLocation } from '@/features/item-location/hooks/useItemLocation'
import {
  useCreateItemUsage,
  useEndItemUsage,
  useGetItem,
  useGetItemLogs,
  useGetItemTransfer,
  useGetItemUsage,
  useUpdateItemStatus,
} from '@/features/items/hooks/useItem'
import { type PatientSearchSelectorRef } from '@/features/patients/components/search/PatientSearchSelector'
import { useGetPatientItems } from '@/features/patients/hooks/usePatient'
import type { Patient } from '@/features/patients/types/patient'
import { useCreateRepair } from '@/features/repairs/hooks/useRepair'
import { HandleTransferDialog } from '@/features/requests/components/HandleTransferDialog'
import {
  useCreateBorrowTransferRequest,
  useCreateReturnTransferRequest,
  useCreateTransferRequest,
  useReceiveTransferRequest,
} from '@/features/requests/hooks/useTransferRequest'
import { useGetConfigs } from '@/features/settings/hooks/useConfig'
import { AppConfigKey } from '@/features/settings/types/config'
import { useDateFormatter } from '@/hooks/useDateFormatter'
import { useFileUploadField } from '@/hooks/useFileUpload'
import { useUI } from '@/hooks/useUI'
import { cn } from '@/lib/utils'
import { toUtcDateString } from '@/utils/date'
import { isApiError } from '@/utils/error'
import { formatBuildingFloorLocation } from '@/utils/location'
import { showError } from '@/utils/toast'
import {
  Activity,
  Building2,
  Calendar,
  CheckCircle2,
  Hash,
  History,
  MapPin,
  Package,
  RefreshCw,
  Store,
  Truck,
  User,
  Wrench,
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { ImageUploadField } from './scan-item/ScanItemShared'
import { RepairSection } from './scan-item/sections/RepairSection'
import { RequestSection } from './scan-item/sections/RequestSection'
import { StatusSection } from './scan-item/sections/StatusSection'
import { TransferOutSection } from './scan-item/sections/TransferOutSection'
import { UsageSection } from './scan-item/sections/UsageSection'

type ScanSection =
  | 'info'
  | 'logs'
  | 'locationLogs'
  | 'request'
  | 'repair'
  | 'status'
  | 'usage'
  | 'receive'
  | 'transferOut'

function ScanSectionCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-xl border border-border/50 bg-card p-4 relative">
      <h2 className="mb-3 text-sm font-semibold text-foreground">{title}</h2>
      {children}
    </div>
  )
}

export function ScanItemContent({ itemId }: { itemId: string }) {
  // Context and base data
  const { t } = useTranslation()
  const { formatDateTime } = useDateFormatter()
  const { itemStatuses, manualItemStatuses, repairPriorities, getEnumLabel } = useUI()
  const { currentUser, departments: userDepartments, refreshMe } = useAuth()
  const { data: configs } = useGetConfigs()
  const { data: item, isLoading, isError, error, isFetching: isFetchingItem, refetch: refetchItem } = useGetItem(itemId)
  const { data: itemLogs = [], isFetching: isFetchingItemLogs, refetch: refetchItemLogs } = useGetItemLogs(itemId)
  const {
    data: itemLocations = [],
    isFetching: isFetchingItemLocations,
    refetch: refetchItemLocations,
  } = useGetItemLocation(itemId, false)

  // Mutations
  const updateItemStatus = useUpdateItemStatus()
  const createRepair = useCreateRepair()
  const createBorrowTransferRequest = useCreateBorrowTransferRequest()
  const createTransferRequest = useCreateTransferRequest()
  const createReturnTransferRequest = useCreateReturnTransferRequest()
  const receiveTransferRequest = useReceiveTransferRequest()
  const createItemUsage = useCreateItemUsage()
  const endItemUsage = useEndItemUsage()

  // Local UI and form state
  const [activeSection, setActiveSection] = useState<ScanSection>('info')
  const [repairForm, setRepairForm] = useState({ title: '', description: '', priority: 'Medium' })
  const [requestForm, setRequestForm] = useState({ quantityRequested: '', targetDepartmentId: '', reason: '' })
  const [newStatus, setNewStatus] = useState<string>('Available')
  const [statusNote, setStatusNote] = useState('')
  const [usageForm, setUsageForm] = useState({ patientId: '', note: '' })
  const [transferOutForm, setTransferOutForm] = useState({
    targetDepartmentId: '',
    reason: '',
    scheduledDate: '',
    deliveryMethod: 'Deliver',
  })
  const [isEndUsageDialogOpen, setIsEndUsageDialogOpen] = useState(false)
  const [showSuccess, setShowSuccess] = useState('')
  const patientSearchSelectorRef = useRef<PatientSearchSelectorRef>(null)

  // Dialog state
  const [handleOpen, setHandleOpen] = useState(false)
  const [selectedTrId, setSelectedTrId] = useState<string | null>(null)

  // Image upload state
  const [repairImages, setRepairImages] = useState<string[]>([])
  const [transferOutImages, setTransferOutImages] = useState<string[]>([])
  const [receiveImages, setReceiveImages] = useState<string[]>([])
  const repairImageField = useFileUploadField({
    value: repairImages,
    onChange: setRepairImages,
    maxFiles: 5,
    onUploadError: error => showError(isApiError(error) ? error.message : t('common.uploadImageError')),
  })
  const transferOutImageField = useFileUploadField({
    value: transferOutImages,
    onChange: setTransferOutImages,
    maxFiles: 4,
    onUploadError: error => showError(isApiError(error) ? error.message : t('common.uploadImageError')),
  })
  const receiveImageField = useFileUploadField({
    value: receiveImages,
    onChange: setReceiveImages,
    maxFiles: 4,
    onUploadError: error => showError(isApiError(error) ? error.message : t('common.uploadImageError')),
  })

  // Dependent data
  const warehouseDepartmentId = configs?.[AppConfigKey.WarehouseDepartmentId]
  const shouldFetchItemTransfer = item?.status === 'PendingTransfer'
  const { data: itemTransfer } = useGetItemTransfer(itemId, shouldFetchItemTransfer)
  const {
    data: patientItems,
    isFetching: isLoadingSelectedPatient,
    error: patientItemsError,
  } = useGetPatientItems(
    usageForm.patientId,
    activeSection === 'usage' && item?.status === 'Available' && !!usageForm.patientId,
  )
  const { data: usageLogs = [] } = useGetItemUsage(itemId, activeSection === 'usage' && item?.status === 'InUse')

  // Derived IDs and options
  const productId = item?.productId
  const ownerDepartmentId = item?.product?.ownerDepartmentId
  const itemDepartmentId = item?.departmentId ?? item?.department?.id ?? ''
  const { data: departments = [], isLoading: isLoadingDepartments } = useGetDepartmentOptions(
    {
      excludeDepartmentId: itemDepartmentId || undefined,
    },
    activeSection === 'transferOut' && !!itemDepartmentId,
  )
  const warehouseDepartment = departments.find(d => d.id === warehouseDepartmentId)
  const transferOutDepartmentGroups: SearchableSelectGroup[] = useMemo(
    () => [
      {
        label: t('requests.mainDepartment'),
        options:
          ownerDepartmentId && ownerDepartmentId !== item?.departmentId
            ? [
                {
                  value: ownerDepartmentId,
                  label: item.product?.ownerDepartmentName ?? '',
                  description: formatBuildingFloorLocation(
                    t,
                    item?.department?.buildingName ?? '',
                    item?.department?.floorName ?? '',
                  ),
                },
              ]
            : [],
      },
      {
        label: t('scans.mainWarehouse'),
        options:
          warehouseDepartment && warehouseDepartment?.id !== itemDepartmentId
            ? [
                {
                  value: warehouseDepartment.id,
                  label: warehouseDepartment.name,
                  description: formatBuildingFloorLocation(
                    t,
                    warehouseDepartment?.buildingName,
                    warehouseDepartment?.floorName,
                  ),
                  searchValue: warehouseDepartment.name,
                },
              ]
            : [],
      },
      {
        label: t('scans.otherDepartments'),
        options: departments
          .filter(department => department.id !== ownerDepartmentId && department.id !== warehouseDepartmentId)
          .map(department => ({
            value: department.id,
            label: department.name,
            description: formatBuildingFloorLocation(t, department.buildingName, department.floorName),
            searchValue: `${department.name} ${department.buildingName || ''} ${department.floorName || ''}`,
          })),
      },
    ],
    [departments, itemDepartmentId, t, warehouseDepartment],
  )
  const requestDepartmentOptions = useMemo(
    () =>
      userDepartments
        .filter(department => department.id !== warehouseDepartmentId && department.id !== ownerDepartmentId)
        .map(department => ({
          value: department.id,
          label: department.name,
          description: formatBuildingFloorLocation(t, department.buildingName, department.floorName),
          searchValue: department.name,
        })),
    [t, userDepartments, warehouseDepartmentId, ownerDepartmentId],
  )

  useEffect(() => {
    if (!showSuccess) return

    const timer = window.setTimeout(() => {
      setShowSuccess('')
    }, 3000)

    return () => window.clearTimeout(timer)
  }, [showSuccess])

  useEffect(() => {
    if (activeSection !== 'request' || requestForm.targetDepartmentId || !itemDepartmentId) return
    if (!requestDepartmentOptions.some(department => department.value === itemDepartmentId)) return

    setRequestForm(current => ({
      ...current,
      targetDepartmentId: itemDepartmentId,
    }))
  }, [activeSection, itemDepartmentId, requestDepartmentOptions, requestForm.targetDepartmentId])

  useEffect(() => {
    if (!patientItemsError) return

    showError(isApiError(patientItemsError) ? patientItemsError.message : t('scans.error.patientNotFound'))
  }, [patientItemsError, t])

  if (isLoading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background p-6 text-center">
        <p className="mt-1 text-sm text-muted-foreground">{t('scans.loadingItem')}</p>
      </div>
    )
  }

  if (!item || isError) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background p-6 text-center">
        <Activity className="size-12 text-muted-foreground/30" />
        <h1 className="mt-4 text-lg font-semibold text-foreground">{t('scans.itemNotFound')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t('scans.itemNotFoundWithId', { id: itemId })}</p>
        {isError && (
          <p className="mt-2 max-w-sm text-xs text-destructive">{error?.message || 'Unable to load item data.'}</p>
        )}
        <a href="/" className="mt-4 text-sm text-primary hover:underline">
          {t('common.backToHome')}
        </a>
      </div>
    )
  }

  // Item status and permission flags
  const canManuallyChangeStatus = manualItemStatuses?.some(status => status.value === item.status) ?? false
  const canOpenUsageFlow = item.status === 'Available' || item.status === 'InUse'
  const config = itemStatusConfig[item.status] ?? itemStatusConfig.Available
  const isItemAvailable = item.status === 'Available'
  const isItemInWarehouse = !!warehouseDepartmentId && itemDepartmentId === warehouseDepartmentId
  const statusLabel = getEnumLabel(itemStatuses, item.status)

  // Submit and upload state
  const isSubmittingRepair = createRepair.isPending || repairImageField.isUploading
  const isSubmittingRequest = createBorrowTransferRequest.isPending
  const isSubmittingTransferOut =
    createTransferRequest.isPending || createReturnTransferRequest.isPending || transferOutImageField.isUploading
  const isReceivingTransfer = receiveTransferRequest.isPending || receiveImageField.isUploading
  const isReturningToOwnerDepartment = transferOutForm.targetDepartmentId === item.product?.ownerDepartmentId

  // Current selections
  const selectedTransferOutDepartment = departments.find(
    department => department.id === transferOutForm.targetDepartmentId,
  )
  const selectedUsagePatient = patientItems?.patient

  // Pending receive state
  const canReceiveItemTransfer =
    !!itemTransfer &&
    // ['Delivering', 'PickupReady'].includes(itemTransfer.status) &&
    userDepartments.some(department => department.id === itemTransfer.targetDepartmentId)
  const pendingTransfer = canReceiveItemTransfer ? itemTransfer : null

  async function handleRepairSubmit() {
    if (!isItemAvailable) return
    if (!repairForm.title || !repairForm.description) return
    try {
      await createRepair.mutateAsync({
        itemId,
        title: repairForm.title,
        description: repairForm.description,
        priority: repairForm.priority,
        imageIds: repairImages,
      })
      setRepairForm({ title: '', description: '', priority: 'Medium' })
      setRepairImages([])
      setShowSuccess(t('scans.success.repair'))
      setActiveSection('info')
    } catch (error) {
      showError(isApiError(error) ? error.message : t('scans.error.repair'))
    }
  }

  async function handleRequestSubmit() {
    if (!productId || !requestForm.targetDepartmentId || !requestForm.reason) return
    try {
      await createBorrowTransferRequest.mutateAsync({
        productId,
        targetDepartmentId: requestForm.targetDepartmentId,
        quantityRequested: requestForm.quantityRequested,
        reason: requestForm.reason,
        imageIds: [],
      })
      setRequestForm({ quantityRequested: '', targetDepartmentId: '', reason: '' })
      setShowSuccess(t('scans.success.request'))
      setActiveSection('info')
    } catch (error) {
      showError(isApiError(error) ? error.message : t('scans.error.request'))
    }
  }

  function handleToggleLogs() {
    if (activeSection === 'logs') {
      setActiveSection('info')
      return
    }

    setActiveSection('logs')
    refetchItemLogs()
  }

  function handleToggleLocationLogs() {
    if (activeSection === 'locationLogs') {
      setActiveSection('info')
      return
    }

    setActiveSection('locationLogs')
    refetchItemLocations()
  }

  async function handleStatusChange() {
    try {
      await updateItemStatus.mutateAsync({
        id: itemId,
        payload: {
          status: newStatus,
          ...(statusNote.trim() ? { note: statusNote.trim() } : {}),
        },
      })
      setStatusNote('')
      setShowSuccess(t('scans.success.status'))
      setActiveSection('info')
    } catch (error) {
      showError(isApiError(error) ? error.message : t('scans.error.status'))
    }
  }

  async function handleUsageLog() {
    if (isItemInWarehouse) return
    if (!usageForm.patientId) return
    if (!selectedUsagePatient?.bedId) return
    if (!usageForm.note.trim()) return
    try {
      await createItemUsage.mutateAsync({
        id: itemId,
        payload: {
          patientId: usageForm.patientId,
          note: usageForm.note.trim(),
        },
      })
      setUsageForm({ patientId: '', note: '' })
      patientSearchSelectorRef.current?.reset()
      setShowSuccess(t('scans.success.usage'))
      setActiveSection('info')
    } catch (error) {
      showError(isApiError(error) ? error.message : t('scans.error.usage'))
    }
  }

  async function handleEndUsage() {
    if (isItemInWarehouse) return
    if (!item || item.status !== 'InUse') return

    try {
      await endItemUsage.mutateAsync({
        id: itemId,
        payload: usageForm.note.trim() ? { note: usageForm.note.trim() } : {},
      })
      setUsageForm({ patientId: '', note: '' })
      patientSearchSelectorRef.current?.reset()
      setIsEndUsageDialogOpen(false)
      setShowSuccess(t('scans.success.endUsage'))
      setActiveSection('info')
    } catch (error) {
      showError(isApiError(error) ? error.message : t('scans.error.endUsage'))
    }
  }

  function handleOpenEndUsageDialog() {
    setUsageForm(current => ({ ...current, note: '' }))
    setIsEndUsageDialogOpen(true)
  }

  function handleSelectPatient(patient: Patient | null) {
    setUsageForm(current => ({
      ...current,
      patientId: patient?.id ?? '',
    }))
  }

  function handleScanPatient(patientId: string) {
    setUsageForm(current => ({
      ...current,
      patientId,
    }))
    patientSearchSelectorRef.current?.reset()
  }

  async function handleConfirmReceive() {
    if (!pendingTransfer) return
    if (receiveImages.length === 0) {
      showError(t('scans.error.attachImage'))
      return
    }

    try {
      await receiveTransferRequest.mutateAsync({
        id: pendingTransfer.id,
        itemId,
        payload: { imageIds: receiveImages },
      })
      setReceiveImages([])
      setShowSuccess(t('scans.success.receive'))
      setActiveSection('info')
    } catch (error) {
      showError(isApiError(error) ? error.message : t('scans.error.receive'))
    }
  }

  async function handleTransferOut() {
    if (!isItemAvailable) return
    if (
      !transferOutForm.targetDepartmentId ||
      !transferOutForm.scheduledDate ||
      !transferOutForm.deliveryMethod ||
      !transferOutForm.reason
    )
      return

    try {
      const payload = {
        productId,
        itemIds: [itemId],
        scheduledDate: toUtcDateString(transferOutForm.scheduledDate),
        deliveryMethod: transferOutForm.deliveryMethod,
        targetDepartmentId: transferOutForm.targetDepartmentId,
        reason: transferOutForm.reason,
        imageIds: transferOutImages,
      }

      if (isReturningToOwnerDepartment) {
        await createReturnTransferRequest.mutateAsync(payload)
      } else {
        await createTransferRequest.mutateAsync(payload)
      }

      setTransferOutForm({ targetDepartmentId: '', reason: '', scheduledDate: '', deliveryMethod: 'Deliver' })
      setTransferOutImages([])
      setShowSuccess(t('scans.success.transfer'))
      setActiveSection('info')
    } catch (error) {
      showError(isApiError(error) ? error.message : t('scans.error.transfer'))
    }
  }

  const actionButtons = [
    {
      section: 'transferOut' as const,
      icon: Truck,
      label: t('scans.action.transferOut'),
      className: 'col-span-2 border-primary/30',
      inactiveClassName: 'bg-primary/5',
      disabled: !isItemAvailable,
      testId: 'scan-item-transfer-button',
    },
    {
      section: 'request' as const,
      icon: Package,
      label: t('scans.action.requestSameModel'),
      testId: 'scan-item-request-button',
    },
    {
      section: 'repair' as const,
      icon: Wrench,
      label: t('scans.action.repair'),
      disabled: !isItemAvailable,
      testId: 'scan-item-report-repair-button',
    },
    {
      section: 'logs' as const,
      icon: History,
      label: t('scans.action.logs'),
      onClick: handleToggleLogs,
      testId: 'scan-item-view-logs-button',
    },
    {
      section: 'locationLogs' as const,
      icon: MapPin,
      label: t('scans.action.locationLogs'),
      onClick: handleToggleLocationLogs,
      testId: 'scan-item-view-location-logs-button',
    },
    {
      section: 'usage' as const,
      icon: User,
      label: t('scans.action.usage'),
      disabled: !canOpenUsageFlow || isItemInWarehouse,
      testId: 'scan-item-record-usage-button',
    },
    {
      section: 'status' as const,
      icon: RefreshCw,
      label: t('scans.action.status'),
      disabled: !canManuallyChangeStatus,
      testId: 'scan-item-change-status-button',
    },
  ]

  return (
    <div data-testid="scan-item-page">
      {/* Success toast */}
      {showSuccess && (
        <div className="fixed top-4 left-1/2 z-50 -translate-x-1/2 rounded-lg bg-success px-4 py-2 text-sm font-medium text-success-foreground shadow-lg">
          {showSuccess}
        </div>
      )}

      <div className="mx-auto max-w-lg p-4">
        {/* Pending Transfer Alert */}
        {pendingTransfer && (
          <div className="mb-4 rounded-xl border border-warning/50 bg-warning/10 p-4">
            <div className="flex items-center gap-2 text-sm font-medium text-warning">
              <Truck className="size-4" />
              {t('scans.pendingReceive.title')}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">{t('scans.pendingReceive.description')}</p>
            <div className="mt-3 flex flex-col gap-2">
              <ImageUploadField
                label={t('scans.pendingReceive.imageLabel')}
                field={receiveImageField}
                maxImages={4}
                uploadText={t('scans.uploadingImage')}
                testId="scan-item-receive-image-upload"
              />
            </div>
            <Button
              className="mt-3 w-full text-xs"
              data-testid="scan-item-receive-confirm-button"
              onClick={handleConfirmReceive}
              disabled={receiveImages.length === 0 || isReceivingTransfer}
            >
              <CheckCircle2 className="mr-1 size-3" />
              {isReceivingTransfer ? t('scans.pendingReceive.submitting') : t('scans.pendingReceive.button')}
            </Button>
          </div>
        )}

        {/* Equipment card */}
        <div className="flex flex-col gap-4 rounded-xl border border-border/50 bg-card p-5">
          <div className="flex items-start justify-between">
            <div className="flex flex-col gap-1">
              <div className="flex items-start gap-2">
                <OnlineStatusDot
                  supportsOnlineStatus={item?.product?.supportsOnlineStatus ?? false}
                  onlineStatus={item.onlineStatus}
                />
                <h1 className="text-base font-bold text-foreground">
                  {item.product?.name ? `${item.product.name} - ${item.serialNumber}` : item.serialNumber}
                </h1>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-6 shrink-0"
                  data-testid="scan-item-refresh-button"
                  onClick={() => {
                    refetchItem()
                    refreshMe()
                    setActiveSection('info')
                    setShowSuccess(t('scans.success.refresh'))
                  }}
                  disabled={isFetchingItem}
                  title={t('scans.refreshItem')}
                >
                  <RefreshCw className={cn('size-3.5', isFetchingItem && 'animate-spin')} />
                </Button>
              </div>

              <p className="text-xs text-muted-foreground">
                {item.product?.brand} {item.product?.model} {item.product?.categoryName}
              </p>
            </div>
            <span
              className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium ${config?.bgColor} ${config?.color}`}
              data-testid="scan-item-status"
            >
              <span className="size-2 rounded-full bg-current" />
              {statusLabel}
            </span>
          </div>

          {/* Details */}
          <div className="grid grid-cols-2 gap-2">
            {[
              { icon: Hash, label: t('scans.field.serialNumber'), value: item.serialNumber },
              { icon: Building2, label: t('scans.field.department'), value: item.department?.name || '-' },
              {
                icon: MapPin,
                label: t('scans.field.buildingFloorLocation'),
                value: item.department?.name
                  ? formatBuildingFloorLocation(
                      t,
                      item.department.buildingName || '-',
                      item.department.floorName || '-',
                    )
                  : '-',
              },
              { icon: Store, label: t('scans.field.vendor'), value: item.vendor?.name || '-' },
              {
                icon: Calendar,
                label: t('scans.field.warrantyExpiry'),
                value: formatDateTime(item.warrantyExpiry, {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                }),
              },
              {
                icon: Wrench,
                label: t('scans.field.nextMaintenance'),
                value: formatDateTime(item.nextMaintenance, {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                }),
              },
            ].map(d => (
              <div key={d.label} className="flex items-center gap-2 rounded-lg bg-secondary/30 p-2.5">
                <d.icon className="size-3.5 shrink-0 text-primary" />
                <div className="flex flex-col">
                  <span className="text-[9px] text-muted-foreground">{d.label}</span>
                  <span className="text-[11px] font-medium text-foreground">{d.value}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Images */}
          {item.imageIds?.length > 0 && (
            <ImageUpload
              files={[]}
              imageIds={item.imageIds}
              onChange={() => {}}
              maxImages={item.imageIds.length}
              readonly
            />
          )}
        </div>

        {/* Action Buttons */}
        <div className="mt-4 grid grid-cols-2 gap-2">
          {actionButtons.map(action => {
            const Icon = action.icon
            const isActive = activeSection === action.section

            return (
              <Button
                key={action.section}
                data-testid={action.testId}
                variant={isActive ? 'default' : 'outline'}
                className={cn('h-12 flex-col gap-1 text-xs', action.className, !isActive && action.inactiveClassName)}
                onClick={action.onClick ?? (() => setActiveSection(isActive ? 'info' : action.section))}
                disabled={action.disabled}
              >
                <Icon className="size-4" />
                {action.label}
              </Button>
            )
          })}
        </div>

        {/* Sections */}
        <div className="mt-4">
          {activeSection === 'transferOut' && (
            <ScanSectionCard title={t('scans.section.transferOut')}>
              <TransferOutSection
                item={item}
                form={transferOutForm}
                setForm={setTransferOutForm}
                departmentGroups={transferOutDepartmentGroups}
                selectedDepartment={selectedTransferOutDepartment}
                imageField={transferOutImageField}
                currentUserName={currentUser?.name}
                isLoadingDepartments={isLoadingDepartments}
                isSubmitting={isSubmittingTransferOut}
                isReturningToOwnerDepartment={isReturningToOwnerDepartment}
                onSubmit={handleTransferOut}
              />
            </ScanSectionCard>
          )}

          {activeSection === 'logs' && (
            <ScanSectionCard title={t('scans.section.history')}>
              <div data-testid="scan-item-logs-section">
                <LogTimeline
                  entries={itemLogs}
                  maxItems={20}
                  isLoading={isFetchingItemLogs}
                  onViewTransferRequest={trId => {
                    setSelectedTrId(trId)
                    setHandleOpen(true)
                  }}
                />
              </div>
            </ScanSectionCard>
          )}

          {activeSection === 'locationLogs' && (
            <ScanSectionCard title={t('scans.section.locationHistory')}>
              <div data-testid="scan-item-location-logs-section">
                <ItemLocations itemLocations={itemLocations} maxItems={10} isLoading={isFetchingItemLocations} />
              </div>
            </ScanSectionCard>
          )}

          {activeSection === 'request' && (
            <ScanSectionCard title={t('scans.section.request')}>
              <RequestSection
                item={item}
                form={requestForm}
                setForm={setRequestForm}
                departmentOptions={requestDepartmentOptions}
                currentUserName={currentUser?.name}
                isSubmitting={isSubmittingRequest}
                onSubmit={handleRequestSubmit}
              />
            </ScanSectionCard>
          )}

          {activeSection === 'repair' && (
            <ScanSectionCard title={t('scans.section.repair')}>
              <RepairSection
                item={item}
                form={repairForm}
                setForm={setRepairForm}
                priorities={repairPriorities}
                imageField={repairImageField}
                currentUserName={currentUser?.name}
                isItemAvailable={isItemAvailable}
                isSubmitting={isSubmittingRepair}
                onSubmit={handleRepairSubmit}
              />
            </ScanSectionCard>
          )}

          {activeSection === 'usage' && !isItemInWarehouse && (
            <ScanSectionCard title={t('scans.section.usage')}>
              <UsageSection
                itemStatus={item.status}
                form={usageForm}
                setForm={setUsageForm}
                patientSearchSelectorRef={patientSearchSelectorRef}
                selectedPatient={selectedUsagePatient}
                isLoadingSelectedPatient={isLoadingSelectedPatient}
                usageLogs={usageLogs}
                isCreatingUsage={createItemUsage.isPending}
                isEndingUsage={endItemUsage.isPending}
                onSelectPatient={handleSelectPatient}
                onScanPatient={handleScanPatient}
                onUsageLog={handleUsageLog}
                onOpenEndUsageDialog={handleOpenEndUsageDialog}
              />
            </ScanSectionCard>
          )}

          {activeSection === 'status' && (
            <ScanSectionCard title={t('scans.section.status')}>
              <StatusSection
                newStatus={newStatus}
                setNewStatus={setNewStatus}
                statusNote={statusNote}
                setStatusNote={setStatusNote}
                manualItemStatuses={manualItemStatuses}
                isSubmitting={updateItemStatus.isPending}
                onSubmit={handleStatusChange}
              />
            </ScanSectionCard>
          )}
        </div>
      </div>
      <BaseDialog
        open={isEndUsageDialogOpen}
        onOpenChange={setIsEndUsageDialogOpen}
        title={t('scans.usage.confirmStopTitle')}
        dialogContentClass="border-border text-foreground sm:max-w-md"
        contentTestId="end-usage-dialog"
        footer={
          <>
            <Button
              type="button"
              variant="outline"
              className="text-xs"
              onClick={() => setIsEndUsageDialogOpen(false)}
              disabled={endItemUsage.isPending}
              data-testid="end-usage-cancel-button"
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              variant="destructive"
              className="text-xs"
              onClick={handleEndUsage}
              disabled={endItemUsage.isPending}
              data-testid="end-usage-confirm-button"
            >
              {endItemUsage.isPending ? t('scans.usage.stopping') : t('scans.usage.confirmStop')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <p className="text-xs text-muted-foreground">{t('scans.usage.confirmStopMessage')}</p>
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs">{t('scans.field.note')}</Label>
            <Textarea
              value={usageForm.note}
              onChange={e => setUsageForm(current => ({ ...current, note: e.target.value }))}
              className="bg-secondary/50 text-xs"
              rows={3}
              placeholder={t('scans.placeholder.endUsageNote')}
              data-testid="end-usage-note"
            />
          </div>
        </div>
      </BaseDialog>

      <HandleTransferDialog transferId={selectedTrId} open={handleOpen} onOpenChange={setHandleOpen} />
    </div>
  )
}
